import React, { useState, forwardRef, useImperativeHandle } from 'react';
import AddressAutofill from './AddressAutofill';

const SavedAddressesTab = forwardRef(({ formData, handleSavedAddressesChange, executiveId, onImmediateSave }, ref) => {
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [newAddressLabel, setNewAddressLabel] = useState('');
  const [newAddress, setNewAddress] = useState({
    addressLine1: '',
    city: '',
    state_province: '',
    postalCode: '',
    countryCode: '',
    countryName: ''
  });
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Helper to save addresses immediately to the backend
  const saveAddressesImmediately = async (updatedAddresses) => {
    // Update local state first for immediate UI feedback
    handleSavedAddressesChange(updatedAddresses);

    // If we have an executiveId and onImmediateSave callback, persist to backend
    if (executiveId && onImmediateSave) {
      setIsSaving(true);
      try {
        await onImmediateSave({ saved_addresses: updatedAddresses });
      } catch (error) {
        console.error('Failed to save address:', error);
        // Could add error handling/toast here
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Add a new saved address
  const handleAddSavedAddress = async () => {
    if (!newAddressLabel.trim() || !newAddress.addressLine1) return false;

    const savedAddress = {
      id: `addr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      label: newAddressLabel.trim(),
      address: { ...newAddress },
      is_default: (formData.saved_addresses || []).length === 0, // First address is default
      place_id: newAddress.place_id || null
    };

    const updatedAddresses = [...(formData.saved_addresses || []), savedAddress];
    await saveAddressesImmediately(updatedAddresses);

    // Reset form
    setNewAddressLabel('');
    setNewAddress({
      addressLine1: '',
      city: '',
      state_province: '',
      postalCode: '',
      countryCode: '',
      countryName: ''
    });
    setShowAddAddress(false);
    return true;
  };

  // Expose method to save pending address when parent form is submitted
  useImperativeHandle(ref, () => ({
    savePendingAddress: () => {
      // Only save if the add form is open and has valid data
      if (showAddAddress && newAddressLabel.trim() && newAddress.addressLine1) {
        // Create the new saved address
        const savedAddress = {
          id: `addr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          label: newAddressLabel.trim(),
          address: { ...newAddress },
          is_default: (formData.saved_addresses || []).length === 0,
          place_id: newAddress.place_id || null
        };

        // Return the new address so parent can include it in save
        return savedAddress;
      }
      return null;
    }
  }));

  // Remove a saved address
  const handleRemoveSavedAddress = async (addressId) => {
    const updatedAddresses = (formData.saved_addresses || []).filter(addr => addr.id !== addressId);
    // If we removed the default, make the first remaining one default
    if (updatedAddresses.length > 0 && !updatedAddresses.some(a => a.is_default)) {
      updatedAddresses[0].is_default = true;
    }
    await saveAddressesImmediately(updatedAddresses);
  };

  // Set an address as default
  const handleSetDefaultAddress = async (addressId) => {
    const updatedAddresses = (formData.saved_addresses || []).map(addr => ({
      ...addr,
      is_default: addr.id === addressId
    }));
    await saveAddressesImmediately(updatedAddresses);
  };

  // Update an existing saved address
  const handleUpdateSavedAddress = async (addressId, updatedAddr) => {
    const updatedAddresses = (formData.saved_addresses || []).map(addr =>
      addr.id === addressId ? { ...addr, address: updatedAddr } : addr
    );
    await saveAddressesImmediately(updatedAddresses);
    setEditingAddressId(null);
  };

  // Update label of an existing saved address (called on every keystroke, so debounce the save)
  const handleUpdateAddressLabel = (addressId, newLabel) => {
    // Update local state immediately for responsive UI
    const updatedAddresses = (formData.saved_addresses || []).map(addr =>
      addr.id === addressId ? { ...addr, label: newLabel } : addr
    );
    handleSavedAddressesChange(updatedAddresses);
    // Note: Label changes will be saved when user clicks "Done" editing
  };

  return (
    <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl shadow-slate-200/50 rounded-3xl p-8 overflow-hidden relative">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50/40 via-transparent to-violet-50/40 pointer-events-none" />

      {/* Header */}
      <div className="relative flex items-center gap-4 mb-8">
        <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/30">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">Saved Addresses</h2>
          <p className="text-slate-500 mt-1">Manage frequently used addresses for quick booking</p>
        </div>
      </div>

      <div className="relative space-y-6">
        {/* Add Address Button */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">
            {(formData.saved_addresses || []).length} saved address{(formData.saved_addresses || []).length !== 1 ? 'es' : ''}
          </span>
          {!showAddAddress && (
            <button
              type="button"
              onClick={() => setShowAddAddress(true)}
              className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 font-medium px-4 py-2 hover:bg-violet-50 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Address
            </button>
          )}
        </div>

        {/* Existing Saved Addresses */}
        {(formData.saved_addresses || []).length > 0 ? (
          <div className="space-y-3">
            {(formData.saved_addresses || []).map((savedAddr) => (
              <div
                key={savedAddr.id}
                className={`border rounded-xl p-4 transition-all ${
                  savedAddr.is_default ? 'border-violet-300 bg-violet-50/50' : 'border-slate-200 bg-white'
                }`}
              >
                {editingAddressId === savedAddr.id ? (
                  // Editing mode
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={savedAddr.label}
                        onChange={(e) => handleUpdateAddressLabel(savedAddr.id, e.target.value)}
                        className="flex-1 h-10 px-3 border border-slate-200 rounded-lg focus:border-violet-400 focus:ring-violet-400 text-sm font-medium"
                        placeholder="Label (e.g., Home, Office)"
                      />
                    </div>
                    <AddressAutofill
                      address={savedAddr.address}
                      onChange={(updatedAddr) => handleUpdateSavedAddress(savedAddr.id, updatedAddr)}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          // Save the current state (including any label changes) before closing edit mode
                          if (executiveId && onImmediateSave) {
                            await saveAddressesImmediately(formData.saved_addresses);
                          }
                          setEditingAddressId(null);
                        }}
                        disabled={isSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isSaving ? 'Saving...' : 'Done'}
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display mode
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-800">{savedAddr.label}</span>
                        {savedAddr.is_default && (
                          <span className="px-2 py-0.5 text-xs font-medium text-violet-700 bg-violet-100 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-slate-600">
                        {savedAddr.address?.addressLine1 && <div>{savedAddr.address.addressLine1}</div>}
                        {(savedAddr.address?.city || savedAddr.address?.state_province || savedAddr.address?.postalCode) && (
                          <div>
                            {[
                              savedAddr.address.city,
                              savedAddr.address.state_province,
                              savedAddr.address.postalCode
                            ].filter(Boolean).join(', ')}
                          </div>
                        )}
                        {savedAddr.address?.countryName && (
                          <div className="text-slate-500">{savedAddr.address.countryName}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!savedAddr.is_default && (
                        <button
                          type="button"
                          onClick={() => handleSetDefaultAddress(savedAddr.id)}
                          className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                          title="Set as default"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setEditingAddressId(savedAddr.id)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit address"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveSavedAddress(savedAddr.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove address"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          !showAddAddress && (
            <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
              <svg className="w-12 h-12 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-slate-500 mb-4">No saved addresses yet</p>
              <button
                type="button"
                onClick={() => setShowAddAddress(true)}
                className="inline-flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 font-medium px-4 py-2 hover:bg-violet-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add your first address
              </button>
            </div>
          )
        )}

        {/* Add New Address Form */}
        {showAddAddress && (
          <div className="border border-violet-200 bg-violet-50/30 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-semibold text-slate-700">Add New Address</h4>
              <button
                type="button"
                onClick={() => {
                  setShowAddAddress(false);
                  setNewAddressLabel('');
                  setNewAddress({
                    addressLine1: '',
                    city: '',
                    state_province: '',
                    postalCode: '',
                    countryCode: '',
                    countryName: ''
                  });
                }}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Label Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Label *</label>
              <input
                type="text"
                value={newAddressLabel}
                onChange={(e) => setNewAddressLabel(e.target.value)}
                className="w-full h-11 px-4 border border-slate-200 rounded-lg focus:border-violet-400 focus:ring-violet-400 text-sm"
                placeholder="e.g., Home, Office"
              />
            </div>

            {/* Address Search */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Address *</label>
              <AddressAutofill
                address={newAddress}
                onChange={(addr) => setNewAddress(addr)}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddAddress(false);
                  setNewAddressLabel('');
                  setNewAddress({
                    addressLine1: '',
                    city: '',
                    state_province: '',
                    postalCode: '',
                    countryCode: '',
                    countryName: ''
                  });
                }}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddSavedAddress}
                disabled={!newAddressLabel.trim() || !newAddress.addressLine1 || isSaving}
                className="px-5 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save Address'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default SavedAddressesTab;
